import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Customer } from '../types';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = window.location.protocol === 'https:' ? '/api' : `http://${window.location.hostname}:3010/api`;

interface EmailGroup {
  groupId: string;
  name: string;
  description: string;
  memberCount: number;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  isDefault: boolean;
}

interface SurveyTemplate {
  templateId: string;
  title: string;
  type: string;
  totalQuestions: number;
}

const QuestionnaireList: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const location = useLocation();
  const initialCustomer = location.state?.customer as Customer;
  const { t } = useLanguage();

  const [customer, setCustomer] = useState<Customer | null>(initialCustomer || null);
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [responses, setResponses] = useState<any[]>([]);
  const [archivedResponses, setArchivedResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendMessage, setSendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Response management states
  const [selectedResponses, setSelectedResponses] = useState<Set<string>>(new Set());
  const [selectedArchived, setSelectedArchived] = useState<Set<string>>(new Set());
  const [showArchive, setShowArchive] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Send dialog states
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingQuestionnaire, setSendingQuestionnaire] = useState<any>(null);
  const [emailGroups, setEmailGroups] = useState<EmailGroup[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [surveyTemplates, setSurveyTemplates] = useState<SurveyTemplate[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('default');
  const [selectedSurveyTemplate, setSelectedSurveyTemplate] = useState('');
  const [surveyPassword, setSurveyPassword] = useState('');
  const [surveyTitle, setSurveyTitle] = useState('');
  const [surveyExpires, setSurveyExpires] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);

  // Email template editor
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [tplName, setTplName] = useState('');
  const [tplSubject, setTplSubject] = useState('');
  const [tplBody, setTplBody] = useState('');

  useEffect(() => {
    if (customerId) loadData();
  }, [customerId]);

  const loadData = async () => {
    try {
      const [customerData, questionnairesData, responsesData, archivedData] = await Promise.all([
        apiClient.getCustomer(customerId!),
        apiClient.getQuestionnaires(),
        apiClient.getResponses(customerId!),
        apiClient.getArchivedResponses(customerId!).catch(() => [])
      ]);
      setCustomer(customerData);
      setQuestionnaires(questionnairesData);
      setResponses(responsesData);
      setArchivedResponses(archivedData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Response management handlers
  const handleToggleSelect = (responseId: string) => {
    setSelectedResponses(prev => {
      const next = new Set(prev);
      if (next.has(responseId)) next.delete(responseId);
      else next.add(responseId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedResponses.size === responses.length) {
      setSelectedResponses(new Set());
    } else {
      setSelectedResponses(new Set(responses.map(r => r.responseId)));
    }
  };

  const handleArchiveSelected = async () => {
    if (selectedResponses.size === 0) return;
    if (!window.confirm(`${selectedResponses.size}件の回答をアーカイブしますか？`)) return;
    setActionLoading(true);
    try {
      await apiClient.bulkArchiveResponses(customerId!, Array.from(selectedResponses));
      setSelectedResponses(new Set());
      setSendMessage({ type: 'success', text: `${selectedResponses.size}件をアーカイブしました` });
      await loadData();
    } catch (error) {
      setSendMessage({ type: 'error', text: 'アーカイブに失敗しました' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveSingle = async (responseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('この回答をアーカイブしますか？')) return;
    setActionLoading(true);
    try {
      await apiClient.archiveResponse(responseId, customerId!);
      setSendMessage({ type: 'success', text: 'アーカイブしました' });
      await loadData();
    } catch (error) {
      setSendMessage({ type: 'error', text: 'アーカイブに失敗しました' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditResponse = (responseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/customer/${customerId}/response/${responseId}?edit=true`, { state: { customer } });
  };

  // Archive management handlers
  const handleToggleArchiveSelect = (responseId: string) => {
    setSelectedArchived(prev => {
      const next = new Set(prev);
      if (next.has(responseId)) next.delete(responseId);
      else next.add(responseId);
      return next;
    });
  };

  const handleSelectAllArchived = () => {
    if (selectedArchived.size === archivedResponses.length) {
      setSelectedArchived(new Set());
    } else {
      setSelectedArchived(new Set(archivedResponses.map(r => r.responseId)));
    }
  };

  const handleRestoreSingle = async (responseId: string) => {
    setActionLoading(true);
    try {
      await apiClient.restoreResponse(responseId, customerId!);
      setSendMessage({ type: 'success', text: '復元しました' });
      await loadData();
    } catch (error) {
      setSendMessage({ type: 'error', text: '復元に失敗しました' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDeleteSingle = async (responseId: string) => {
    if (!window.confirm('この回答を完全に削除しますか？この操作は取り消せません。')) return;
    setActionLoading(true);
    try {
      await apiClient.deleteResponse(responseId, customerId!, true);
      setSendMessage({ type: 'success', text: '完全に削除しました' });
      await loadData();
    } catch (error) {
      setSendMessage({ type: 'error', text: '削除に失敗しました' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedArchived.size === 0) return;
    if (!window.confirm(`${selectedArchived.size}件を完全に削除しますか？この操作は取り消せません。`)) return;
    setActionLoading(true);
    try {
      await apiClient.bulkDeleteResponses(customerId!, Array.from(selectedArchived));
      setSelectedArchived(new Set());
      setSendMessage({ type: 'success', text: `${selectedArchived.size}件を完全に削除しました` });
      await loadData();
    } catch (error) {
      setSendMessage({ type: 'error', text: '削除に失敗しました' });
    } finally {
      setActionLoading(false);
    }
  };

  const loadSendDialogData = async () => {
    try {
      const [groupsRes, emailTplRes, surveyTplRes] = await Promise.all([
        fetch(`${API_URL}/email-groups`).then(r => r.json()),
        fetch(`${API_URL}/email-templates`).then(r => r.json()),
        fetch(`${API_URL}/survey-templates`).then(r => r.json())
      ]);
      setEmailGroups(groupsRes.groups || []);
      setEmailTemplates(emailTplRes.templates || []);
      setSurveyTemplates(surveyTplRes.templates || []);
    } catch (err) {
      console.error('Failed to load send dialog data:', err);
    }
  };

  const handleOpenSendDialog = (q: any) => {
    setSendingQuestionnaire(q);
    setSurveyTitle(`${customer?.customerName || ''} - ${q.title}`);
    setSelectedSurveyTemplate(`questionnaire:${q.questionnaireId}`);
    setSelectedGroup('');
    setAdditionalEmails(customer?.email || '');
    setSelectedEmailTemplate('default');
    setSurveyPassword('');
    setSurveyExpires('');
    setSendResult(null);
    setShowSendDialog(true);
    loadSendDialogData();
  };

  const handleSend = async () => {
    if (!surveyTitle.trim()) {
      alert('タイトルを入力してください');
      return;
    }
    if (!selectedGroup && !additionalEmails.trim()) {
      alert('送信先グループまたはメールアドレスを指定してください');
      return;
    }

    // Parse additional emails
    const emails: { email: string; name: string }[] = [];
    if (additionalEmails.trim()) {
      const parts = additionalEmails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
      for (const part of parts) {
        emails.push({ email: part, name: part });
      }
    }

    setSending(true);
    try {
      const baseUrl = window.location.origin;
      const res = await fetch(`${API_URL}/surveys/send-to-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: selectedSurveyTemplate,
          title: surveyTitle.trim(),
          groupId: selectedGroup || undefined,
          emails: emails.length > 0 ? emails : undefined,
          emailTemplateId: selectedEmailTemplate,
          password: surveyPassword || undefined,
          expiresAt: surveyExpires ? new Date(surveyExpires).toISOString() : undefined,
          baseUrl
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSendResult(data);
        setSendMessage({ type: 'success', text: `アンケートを${data.survey.respondents.length}人に送信しました` });
      } else {
        const err = await res.json();
        alert('送信に失敗しました: ' + (err.error?.message || ''));
      }
    } catch (err) {
      alert('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setSendMessage({ type: 'success', text: 'リンクをコピーしました' });
  };

  const handleSaveEmailTemplate = async () => {
    if (!tplName.trim() || !tplSubject.trim() || !tplBody.trim()) {
      alert('全ての項目を入力してください');
      return;
    }
    try {
      if (editingTemplate && !editingTemplate.isDefault) {
        await fetch(`${API_URL}/email-templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: tplName, subject: tplSubject, body: tplBody })
        });
      } else {
        await fetch(`${API_URL}/email-templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: tplName, subject: tplSubject, body: tplBody })
        });
      }
      setShowTemplateEditor(false);
      loadSendDialogData();
    } catch (err) {
      alert('テンプレートの保存に失敗しました');
    }
  };

  const handleDeleteEmailTemplate = async (id: string) => {
    if (!window.confirm('このテンプレートを削除しますか？')) return;
    try {
      await fetch(`${API_URL}/email-templates/${id}`, { method: 'DELETE' });
      loadSendDialogData();
    } catch (err) {
      alert('削除に失敗しました');
    }
  };

  const handleStartQuestionnaire = (questionnaireId: string) => {
    navigate(`/customer/${customerId}/questionnaire/${questionnaireId}`, { state: { customer } });
  };

  const handleViewResponse = (responseId: string) => {
    navigate(`/customer/${customerId}/response/${responseId}`, { state: { customer } });
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>{t('common', 'loading')}</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate('/')} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          {t('questionnaireList', 'backToCustomers')}
        </button>
      </div>

      <h2>{customer?.customerName} - {t('questionnaireList', 'management')}</h2>

      {/* Send Status Message */}
      {sendMessage && (
        <div style={{
          padding: '10px 16px', borderRadius: '4px', marginBottom: '16px',
          backgroundColor: sendMessage.type === 'success' ? '#d4edda' : '#f8d7da',
          color: sendMessage.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${sendMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: '14px'
        }}>
          {sendMessage.text}
          <button onClick={() => setSendMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>×</button>
        </div>
      )}

      {/* Available Questionnaires */}
      <div style={{ marginBottom: '40px' }}>
        <h3>{t('questionnaireList', 'available')}</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          {questionnaires.map((q) => (
            <div key={q.questionnaireId} style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>{q.title}</h4>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => handleStartQuestionnaire(q.questionnaireId)} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                  {t('questionnaireList', 'startNew')}
                </button>
                <button onClick={() => handleOpenSendDialog(q)} style={{ padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                  {t('questionnaireList', 'sendByEmail')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Past Responses & Archive Tabs */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, cursor: 'pointer', color: !showArchive ? '#333' : '#999', borderBottom: !showArchive ? '2px solid #007bff' : 'none', paddingBottom: '4px' }}
            onClick={() => { setShowArchive(false); setSelectedArchived(new Set()); }}>
            {t('questionnaireList', 'responseHistory')} ({responses.length})
          </h3>
          <h3 style={{ margin: 0, cursor: 'pointer', color: showArchive ? '#333' : '#999', borderBottom: showArchive ? '2px solid #dc3545' : 'none', paddingBottom: '4px' }}
            onClick={() => { setShowArchive(true); setSelectedResponses(new Set()); }}>
            アーカイブ ({archivedResponses.length})
          </h3>
        </div>

        {!showArchive ? (
          /* Active Responses */
          <>
            {responses.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input type="checkbox" checked={selectedResponses.size === responses.length && responses.length > 0} onChange={handleSelectAll} />
                  全て選択
                </label>
                {selectedResponses.size > 0 && (
                  <button onClick={handleArchiveSelected} disabled={actionLoading}
                    style={{ padding: '4px 12px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {actionLoading ? '処理中...' : `選択した${selectedResponses.size}件を削除`}
                  </button>
                )}
                <span style={{ fontSize: '12px', color: '#888', marginLeft: 'auto' }}>削除した回答はアーカイブに移動します</span>
              </div>
            )}
            {responses.length === 0 ? (
              <p style={{ color: '#666' }}>{t('questionnaireList', 'noResponses')}</p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {responses.map((response) => (
                  <div key={response.responseId}
                    style={{ backgroundColor: selectedResponses.has(response.responseId) ? '#e8f4fd' : '#fff', padding: '15px', borderRadius: '4px', border: `1px solid ${selectedResponses.has(response.responseId) ? '#007bff' : '#ddd'}`, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input type="checkbox" checked={selectedResponses.has(response.responseId)}
                      onChange={() => handleToggleSelect(response.responseId)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleViewResponse(response.responseId)}
                      onMouseEnter={(e) => { (e.currentTarget.parentElement as HTMLElement).style.borderColor = '#007bff'; }}
                      onMouseLeave={(e) => { if (!selectedResponses.has(response.responseId)) (e.currentTarget.parentElement as HTMLElement).style.borderColor = '#ddd'; }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('questionnaireList', 'questionnaire')} {response.questionnaireId}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {response.answeredQuestions} / {response.totalQuestions} {t('questionnaireList', 'answered')} ({response.completionRate.toFixed(1)}%)
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>{new Date(response.createdAt).toLocaleDateString('ja-JP')}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={(e) => handleEditResponse(response.responseId, e)} title="編集"
                        style={{ padding: '6px 10px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                        編集
                      </button>
                      <button onClick={(e) => handleArchiveSingle(response.responseId, e)} title="削除" disabled={actionLoading}
                        style={{ padding: '6px 10px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                        削除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Archived Responses */
          <>
            {archivedResponses.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input type="checkbox" checked={selectedArchived.size === archivedResponses.length && archivedResponses.length > 0} onChange={handleSelectAllArchived} />
                  全て選択
                </label>
                {selectedArchived.size > 0 && (
                  <button onClick={handleBulkPermanentDelete} disabled={actionLoading}
                    style={{ padding: '4px 12px', backgroundColor: '#721c24', color: '#fff', border: 'none', borderRadius: '4px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {actionLoading ? '処理中...' : `選択した${selectedArchived.size}件を完全削除`}
                  </button>
                )}
                <span style={{ fontSize: '12px', color: '#856404', marginLeft: 'auto' }}>完全削除すると復元できません</span>
              </div>
            )}
            {archivedResponses.length === 0 ? (
              <p style={{ color: '#666' }}>アーカイブされた回答はありません</p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {archivedResponses.map((response) => (
                  <div key={response.responseId}
                    style={{ backgroundColor: selectedArchived.has(response.responseId) ? '#ffeeba' : '#f8f9fa', padding: '15px', borderRadius: '4px', border: `1px solid ${selectedArchived.has(response.responseId) ? '#ffc107' : '#ddd'}`, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '12px', opacity: 0.85 }}>
                    <input type="checkbox" checked={selectedArchived.has(response.responseId)}
                      onChange={() => handleToggleArchiveSelect(response.responseId)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>{t('questionnaireList', 'questionnaire')} {response.questionnaireId}</div>
                          <div style={{ fontSize: '14px', color: '#888' }}>
                            {response.answeredQuestions} / {response.totalQuestions} {t('questionnaireList', 'answered')} ({response.completionRate.toFixed(1)}%)
                          </div>
                          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                            アーカイブ日: {response.archivedAt ? new Date(response.archivedAt).toLocaleDateString('ja-JP') : '-'}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#888' }}>{new Date(response.createdAt).toLocaleDateString('ja-JP')}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => handleRestoreSingle(response.responseId)} title="復元" disabled={actionLoading}
                        style={{ padding: '6px 10px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                        復元
                      </button>
                      <button onClick={() => handlePermanentDeleteSingle(response.responseId)} title="完全削除" disabled={actionLoading}
                        style={{ padding: '6px 10px', backgroundColor: '#721c24', color: '#fff', border: 'none', borderRadius: '4px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                        完全削除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Send Dialog */}
      {showSendDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto' }}>
            {sendResult ? (
              /* Results view */
              <div>
                <h3 style={{ margin: '0 0 16px', color: '#28a745' }}>送信完了</h3>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>{sendResult.survey.respondents.length}人にアンケートを送信しました</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {sendResult.links.map((link: any, idx: number) => (
                    <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontSize: '0.85rem', marginBottom: '4px' }}><strong>{link.name}</strong> ({link.email})</div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input type="text" value={link.url} readOnly style={{ flex: 1, padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.75rem', color: '#555', backgroundColor: '#f7fafc' }} />
                        <button onClick={() => handleCopyLink(link.url)} style={{ padding: '4px 10px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}>コピー</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <button onClick={() => { setShowSendDialog(false); setSendResult(null); }} style={{ padding: '8px 16px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>閉じる</button>
                </div>
              </div>
            ) : (
              /* Send form */
              <div>
                <h3 style={{ margin: '0 0 20px' }}>アンケート送信 - {sendingQuestionnaire?.title}</h3>

                {/* Survey Template Selection */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>質問テンプレート</label>
                  <select value={selectedSurveyTemplate} onChange={(e) => setSelectedSurveyTemplate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
                    {questionnaires.map(q => (
                      <option key={q.questionnaireId} value={`questionnaire:${q.questionnaireId}`}>{q.title}</option>
                    ))}
                    {surveyTemplates.map(t => (
                      <option key={t.templateId} value={t.templateId}>{t.title} ({t.totalQuestions}問)</option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>アンケートタイトル</label>
                  <input type="text" value={surveyTitle} onChange={(e) => setSurveyTitle(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                {/* Group Selection */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>送信先グループ</label>
                  <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
                    <option value="">グループを選択（任意）</option>
                    {emailGroups.map(g => (
                      <option key={g.groupId} value={g.groupId}>{g.name} ({g.memberCount}人)</option>
                    ))}
                  </select>
                </div>

                {/* Additional Emails */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>追加メールアドレス（カンマ区切り）</label>
                  <textarea value={additionalEmails} onChange={(e) => setAdditionalEmails(e.target.value)}
                    placeholder="example@email.com, another@email.com"
                    rows={2}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>

                {/* Email Template */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                    メールテンプレート
                    <button onClick={() => { setShowTemplateEditor(true); setEditingTemplate(null); setTplName(''); setTplSubject(''); setTplBody(''); }}
                      style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      + 新規作成
                    </button>
                  </label>
                  <select value={selectedEmailTemplate} onChange={(e) => setSelectedEmailTemplate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
                    {emailTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {/* Preview selected template */}
                  {emailTemplates.find(t => t.id === selectedEmailTemplate) && (
                    <div style={{ marginTop: '6px', padding: '8px', backgroundColor: '#f7fafc', borderRadius: '4px', fontSize: '0.75rem', color: '#4a5568', maxHeight: '80px', overflow: 'auto' }}>
                      <div><strong>件名:</strong> {emailTemplates.find(t => t.id === selectedEmailTemplate)?.subject}</div>
                      <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{emailTemplates.find(t => t.id === selectedEmailTemplate)?.body.substring(0, 150)}...</div>
                    </div>
                  )}
                </div>

                {/* Password */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>パスワード（任意）</label>
                  <input type="text" value={surveyPassword} onChange={(e) => setSurveyPassword(e.target.value)}
                    placeholder="回答時に必要なパスワード"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#718096' }}>設定するとリンクを開いた際にパスワード入力が必要になります</p>
                </div>

                {/* Expires */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>回答期限</label>
                  <input type="date" value={surveyExpires} onChange={(e) => setSurveyExpires(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }} />
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowSendDialog(false)} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>キャンセル</button>
                  <button onClick={handleSend} disabled={sending} style={{ padding: '10px 20px', backgroundColor: sending ? '#6c757d' : '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: sending ? 'not-allowed' : 'pointer' }}>
                    {sending ? '送信中...' : '送信'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Template Editor Modal */}
      {showTemplateEditor && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px' }}>{editingTemplate ? 'テンプレート編集' : '新規メールテンプレート'}</h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>テンプレート名</label>
              <input type="text" value={tplName} onChange={(e) => setTplName(e.target.value)}
                placeholder="例: カスタムテンプレート"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>件名</label>
              <input type="text" value={tplSubject} onChange={(e) => setTplSubject(e.target.value)}
                placeholder="例: 【EA-QA】{title} のご回答のお願い"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>本文</label>
              <textarea value={tplBody} onChange={(e) => setTplBody(e.target.value)}
                rows={8}
                placeholder={'{name} 様\n\nアンケートへのご回答をお願いいたします。\n\n回答URL: {url}\n期限: {deadline}'}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }} />
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#718096' }}>
                変数: {'{name}'} = 宛名, {'{title}'} = タイトル, {'{url}'} = 回答URL, {'{deadline}'} = 期限, {'{password}'} = パスワード
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowTemplateEditor(false)} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>キャンセル</button>
              <button onClick={handleSaveEmailTemplate} style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>保存</button>
            </div>

            {/* Existing templates list */}
            {emailTemplates.length > 0 && (
              <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <h4 style={{ fontSize: '0.9rem', marginBottom: '10px' }}>既存テンプレート</h4>
                {emailTemplates.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: '0.85rem' }}>{t.name} {t.isDefault && <span style={{ color: '#999', fontSize: '0.7rem' }}>(プリセット)</span>}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => { setEditingTemplate(t); setTplName(t.name); setTplSubject(t.subject); setTplBody(t.body); }}
                        style={{ fontSize: '0.75rem', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer' }}>編集</button>
                      {!t.isDefault && (
                        <button onClick={() => handleDeleteEmailTemplate(t.id)}
                          style={{ fontSize: '0.75rem', color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer' }}>削除</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireList;
